import { Button,Skeleton,Stack,Typography } from '@mui/material';
import { ThemeProvider } from '@mui/material/styles';
import { useQuery } from '@tanstack/react-query';
import MUIDataTable,{ MUIDataTableOptions } from 'mui-datatables';
import { useEffect,useState } from 'react';
import { useTranslation } from 'react-i18next';
import { toast } from 'react-toastify';
import AttToken from '../../helpers/attToken';
import { trackEventMatomo } from '../../helpers/matomo';
import PrintService from '../../service/PrintTemplate';
import { PrintTemplateType } from '../../types/print-template/types';
import { getMuiTheme,tableOptions } from "../TableHelpers/options";
import PrintTemplateModal from './Modals/PrintTemplateModal';

const printService = new PrintService();

export default function PrintTemplate() {
  const initialPrintTemplate = {
    name: "Novo template",
    dafaultTemplate: false,
    cliente: "",
    headerText: {
      bold: false,
      italic: false,
      size: 10,
      text: "",
    },
    footerText: {
      bold: false,
      italic: false,
      size: 10,
    },
    color: "#ffffff",
    logo: "",
    status: false,
  };
  const [selectedPrint, setSelectedPrint] = useState<PrintTemplateType | null>(initialPrintTemplate);
  const [open, setOpen] = useState(false);
  const { t } = useTranslation('translation');



  const { data, isLoading, isFetching, isError, isSuccess } = useQuery({
    queryKey: ['GET_PRINT_TEMPLATE'],
    queryFn: async () => {
      toast.info('Buscando lista de modelos de impressão...', { toastId: 'buscando' });
      const token = await AttToken();
      if (token) {
        const response = await printService.getAllTemplates(token);
        return response.printTemplates;
      }
    },
    retry: 2,
    refetchOnMount: 'always',
    refetchOnWindowFocus: false
  });

  const loading = isLoading || isFetching;

  useEffect(() => {
    if (data && isSuccess) {
      toast.success('Modelos de impressão encontrados', { toastId: 'sucesso' });
    } if (isError) {
      toast.error('Erro ao buscar modelos de impressão', { toastId: 'erro' });
    }
  }, [loading, isSuccess, isError, data]);


  const columns = [
    {
      name: "name",
      label: t('name'),
      options: {
        filter: true,
        sort: true,
        sortThirdClickReset: true
      }
    },
    {
      name: "status",
      label: t('status'),
      options: {
        customBodyRender: (value: string) => (
          value ? <span className='tag is-primary'>{t('active')}</span> : <span className='tag is-danger'>{t('inactive')}</span>
        ),
        filter: true,
        sort: true,
        sortThirdClickReset: true
      }
    },
    {
      name: "id",
      label: t('edit'),
      options: {
        display: false,
        filter: true,
        sort: false,
      }
    },
  ];
  const translatedTextLabels: MUIDataTableOptions = {
    onRowClick: (rowData) => {
      trackEventMatomo('Configurar impresões', 'click', 'td', 'abre edição de modelo');
      const printData = data?.find((elem: PrintTemplateType) => elem.id === rowData[2]);
      if (printData) {
        setSelectedPrint(printData);
        setOpen(true);
      }
    },
    textLabels: {
      body: {
        noMatch: `${t('table.noMatch')}`,
        toolTip: `${t('table.toolTip')}`,
        columnHeaderTooltip: (column) =>
          `${t('table.columnHeaderTooltip')} ${column.label}`
      },
      pagination: {
        next: `${t('table.next')}`,
        previous: `${t('table.previous')}`,
        rowsPerPage: `${t('table.rowsPerPage')}`,
        displayRows: `${t('table.displayRows')}`
      },
      toolbar: {
        search: `${t('table.search')}`,
        filterTable: `${t('table.filterTable')}`
      },
      filter: {
        title: `${t('table.title')}`,
        reset: `${t('table.reset')}`
      },
      viewColumns: {
        title: `${t('table.viewColumnsTitle')}`
      }
    }
  };

  function handleNew() {
    setSelectedPrint({
      name: 'Novo template',
      dafaultTemplate: false,
      cliente: undefined,
      status: true,
    });
    setOpen(true);
  }

  return (
    <Stack direction='column' justifyContent='flex-start' alignItems='flex-start' spacing={1} width='100%'>
      {open && (
        <PrintTemplateModal
          open={open}
          setOpen={setOpen}
          selectedPrint={selectedPrint as PrintTemplateType}
          setSelectedPrint={setSelectedPrint}
        />
      )}
      <Typography variant='h6' width='100%' color='textDisabled' textAlign='center' fontWeight={600}>
        {t('checklists.checklistReportTemplateConfiguration')}
      </Typography>
      <Button
        variant='contained'
        color='success'
        onClick={() => {
          trackEventMatomo('Configurar impresões', 'click', 'button', 'abre criação de modelo');
          handleNew();
        }}
      >
        {`${t('checklists.printConfig.newTemplate')} +`}
      </Button>
      {loading
        ? <Skeleton variant='rounded' width='100%' height={400} />
        : <Stack width='100%'>
          <ThemeProvider theme={getMuiTheme()}>
            <MUIDataTable
              title={t('checklists.printConfig.printTemplates')}
              columns={columns}
              data={data}
              options={{ ...tableOptions, ...translatedTextLabels }}
            />
          </ThemeProvider>
        </Stack>}
    </Stack>
  );
}
